/**
 * SelectionScorecard — the V7 report's headline numbers: Familiarity, Trust,
 * Relevance, and Overall Selection Readiness™ (0–100, directional), each with
 * its one-line explanation. Styled like BenchmarkScorecard/ScoreCard so the
 * audit reads as one product. Scores are directional LLM judgments grounded in
 * the evidence — not deterministic measurements.
 */
import type { SelectionScorecard as Scorecard } from '@aurora/shared';

/** Map a 0–100 directional score onto the shared band palette. */
function band(score: number): string {
  if (score >= 75) return 'strong';
  if (score >= 55) return 'solid';
  if (score >= 35) return 'moderate';
  return 'low';
}

function Metric({
  label,
  score,
  why,
  emphasis = false,
}: {
  label: string;
  score: number;
  why: string;
  emphasis?: boolean;
}): JSX.Element {
  const pct = `${Math.max(0, Math.min(100, score))}%`;
  return (
    <div
      className={`score-card band-${band(score)}${emphasis ? ' score-card-overall' : ''}`}
    >
      <div className="score-head">
        <span className="score-label">{label}</span>
        <span className="score-value">{score}</span>
      </div>
      <div className="score-bar" aria-hidden>
        <div className="score-bar-fill" style={{ width: pct }} />
      </div>
      <p className="score-why">{why}</p>
    </div>
  );
}

export function SelectionScorecard({
  scorecard,
}: {
  scorecard: Scorecard;
}): JSX.Element {
  return (
    <div>
      <div className="score-grid">
        <Metric
          label="Familiarity"
          score={scorecard.familiarity.score}
          why={scorecard.familiarity.explanation}
        />
        <Metric
          label="Trust"
          score={scorecard.trust.score}
          why={scorecard.trust.explanation}
        />
        <Metric
          label="Relevance"
          score={scorecard.relevance.score}
          why={scorecard.relevance.explanation}
        />
        <Metric
          label="Overall Selection Readiness™"
          score={scorecard.overall.score}
          why={scorecard.overall.explanation}
          emphasis
        />
      </div>
      <div className="scorecard-summary">
        <span>
          <strong>Strongest:</strong> {scorecard.strongestDimension}
        </span>
        <span>
          <strong>Weakest:</strong> {scorecard.weakestDimension}
        </span>
        <span>
          <strong>Primary driver:</strong> {scorecard.primaryDriver}
        </span>
        <span>
          <strong>Primary barrier:</strong> {scorecard.primaryBarrier}
        </span>
      </div>
    </div>
  );
}
